/**
 * Pantalla de acceso — dirección «Llave».
 *
 * Es la única pantalla que se ve sin sesión, así que carga con todo el peso de la primera
 * impresión. No lleva tarjeta: el formulario lo sostiene la tipografía, y el pico del logo
 * entra a escala de edificio por la izquierda. Lo que se mueve, se mueve una vez y hacia
 * dentro; nada parpadea después.
 *
 * LO IMPORTANTE, Y NO ES ESTÉTICO: la pantalla pregunta PRIMERO por la puerta —panel de
 * propietario o portal de inquilino— y esa elección VIAJA AL SERVIDOR, que la comprueba
 * contra el rol de la cuenta (`WRONG_AUDIENCE`). Un selector que solo cambiase el texto sería
 * decoración: dejaría entrar a un inquilino por la puerta de propietario para soltarlo, tres
 * pantallas después, en un portal que no había pedido.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { AUDIENCE_LABELS, LOGIN_AUDIENCES, type LoginAudience } from '@naar/shared';
import { Icon } from '../components/Icon';
import { useAuth } from '../auth/AuthContext';
import { SessionError } from '../api/session';
import logo from '../assets/naar-logo.png';
import styles from './LoginPage.module.css';

/**
 * Las cuentas de demostración solo se enseñan en desarrollo, o si el build se pide con
 * `VITE_DEMO_ACCOUNTS=true` (una demo pública a propósito). En un build de producción normal
 * el bloque desaparece, y con él la contraseña de la siembra, que el minificador elimina al
 * quedar sin uso: publicarla en la pantalla de acceso de un despliegue real es regalar la
 * cuenta de administrador.
 */
const SHOW_DEMO_ACCOUNTS = import.meta.env.DEV || import.meta.env.VITE_DEMO_ACCOUNTS === 'true';

/** Coinciden con `apps/api/prisma/seed.ts`. */
const DEMO_PASSWORD = 'naar-demo-2026';

const DEMO_ACCOUNTS: Record<LoginAudience, { email: string; role: string; hint: string }[]> = {
  owner: [
    { email: 'admin@naar.es', role: 'Administrador', hint: 'todo' },
    { email: 'gestion@naar.es', role: 'Gestor', hint: 'crea y edita; no borra ni rescinde' },
  ],
  tenant: [{ email: 'laura.martin@example.com', role: 'Inquilina', hint: 'solo el portal' }],
};

const COPY: Record<LoginAudience, { intro: string; placeholder: string; cta: string; foot: string }> = {
  owner: {
    intro: 'Te llevamos a tu cartera: inmuebles, contratos, cobros y los números del mes.',
    placeholder: 'tu@correo.es',
    cta: 'Entrar al panel',
    foot: '¿Has olvidado la contraseña? Pídesela a quien administra vuestra organización.',
  },
  tenant: {
    intro: 'Te llevamos a tu portal: tu próximo pago, tus incidencias y el chat con la gestión.',
    placeholder: 'tu@correo.es',
    cta: 'Entrar a mi portal',
    foot: '¿Todavía sin acceso? Pídeselo al propietario: te lo crea desde su pantalla de Inquilinos.',
  },
};

const AUDIENCE_STORAGE_KEY = 'naar.login.audience';

/** Los dos picos. Cada uno lo dibujan tres trazos: banda, filete y reflejo. */
const PEAK_A = 'M-200 990 L250 170 L700 990';
const PEAK_B = 'M-20 990 L430 170 L880 990';

/** La última puerta usada en este navegador. Es una comodidad, nunca una credencial. */
function readStoredAudience(): LoginAudience {
  try {
    const stored = window.localStorage.getItem(AUDIENCE_STORAGE_KEY);
    if (stored === 'owner' || stored === 'tenant') return stored;
  } catch {
    /* Modo privado o cookies bloqueadas: se entra por la puerta de siempre. */
  }
  return 'owner';
}

export function LoginPage() {
  const { login } = useAuth();
  const [audience, setAudience] = useState<LoginAudience>(readStoredAudience);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<{ message: string; suggested: LoginAudience | null } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const copy = COPY[audience];
  const demoAccounts = useMemo(() => DEMO_ACCOUNTS[audience], [audience]);

  // Autofoco solo en escritorio: en un móvil abriría el teclado y taparía media pantalla.
  useEffect(() => {
    if (window.matchMedia('(min-width: 900px)').matches) emailRef.current?.focus();
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(AUDIENCE_STORAGE_KEY, audience);
    } catch {
      /* Da igual: es una comodidad. */
    }
  }, [audience]);

  /**
   * Sacude el formulario reiniciando la animación.
   *
   * Se quita la clase y se vuelve a poner en el siguiente fotograma en vez de forzar un
   * reflow sincrónico leyendo `offsetWidth`, que es el truco clásico y cuesta un cálculo de
   * estilos completo cada vez que alguien se equivoca de contraseña.
   */
  function shake() {
    const element = formRef.current;
    const shakeClass = styles.shake;
    if (!element || !shakeClass) return;
    element.classList.remove(shakeClass);
    requestAnimationFrame(() => element.classList.add(shakeClass));
  }

  function fail(message: string, suggested: LoginAudience | null = null) {
    setError({ message, suggested });
    shake();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.trim()) {
      emailRef.current?.focus();
      return fail('Escribe tu correo electrónico.');
    }
    if (!password) {
      passwordRef.current?.focus();
      return fail('Escribe tu contraseña.');
    }

    setSubmitting(true);
    try {
      await login(email.trim(), password, audience);
    } catch (cause) {
      /**
       * `WRONG_AUDIENCE` no es un fallo de credenciales: la contraseña era correcta y la
       * persona se ha equivocado de puerta. Merece que la pantalla se lo arregle de un clic en
       * vez de dejarla probando combinaciones.
       */
      if (cause instanceof SessionError && cause.code === 'WRONG_AUDIENCE') {
        const detail = cause.details as { audience?: LoginAudience } | undefined;
        fail(cause.message, detail?.audience ?? null);
      } else {
        fail(cause instanceof Error ? cause.message : 'No se ha podido iniciar sesión.');
        emailRef.current?.focus();
      }
    } finally {
      setSubmitting(false);
    }
  }

  function switchAudience(next: LoginAudience) {
    setAudience(next);
    setError(null);
  }

  function fillDemo(demoEmail: string) {
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    setError(null);
    passwordRef.current?.focus();
  }

  return (
    <div className={styles.shell}>
      <PeakMark />

      <header className={styles.brand}>
        <img src={logo} alt="NAAR Real State" width={260} height={106} />
        <span className={styles.tagline}>Gestión inmobiliaria</span>
      </header>

      <main className={styles.panel}>
        <form ref={formRef} className={styles.form} onSubmit={submit} noValidate>
          <p className={styles.eyebrow} style={{ '--i': 0 } as CSSProperties}>
            Acceso
          </p>
          <h1 className={styles.title} style={{ '--i': 1 } as CSSProperties}>
            Entra
          </h1>

          {/* La clave hace que el texto se renueve con la animación al cambiar de puerta. */}
          <p
            key={audience}
            className={styles.intro}
            style={{ '--i': 2 } as CSSProperties}
          >
            {copy.intro}
          </p>

          <div
            className={styles.roles}
            style={{ '--i': 3 } as CSSProperties}
            role="group"
            aria-label="Cómo entras"
          >
            <span
              className={styles.rolesIndicator}
              data-audience={audience}
              aria-hidden="true"
            />
            {LOGIN_AUDIENCES.map((option) => (
              <button
                key={option}
                type="button"
                className={styles.role}
                aria-pressed={audience === option}
                onClick={() => switchAudience(option)}
              >
                {AUDIENCE_LABELS[option]}
              </button>
            ))}
          </div>

          <div className={styles.field} style={{ '--i': 4 } as CSSProperties}>
            <label htmlFor="login-email">Correo</label>
            <input
              ref={emailRef}
              id="login-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder={copy.placeholder}
              required
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'login-error' : undefined}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className={styles.field} style={{ '--i': 5 } as CSSProperties}>
            <label htmlFor="login-password">Contraseña</label>
            <div className={styles.password}>
              <input
                ref={passwordRef}
                id="login-password"
                name="password"
                type={reveal ? 'text' : 'password'}
                autoComplete="current-password"
                spellCheck={false}
                placeholder="••••••••••••"
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'login-error' : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyUp={(event) => setCapsLock(event.getModifierState?.('CapsLock') ?? false)}
              />
              <button
                type="button"
                className={styles.peek}
                onClick={() => setReveal((value) => !value)}
                aria-label={reveal ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                aria-pressed={reveal}
              >
                <Icon name={reveal ? 'eye-off' : 'eye'} size={18} />
              </button>
            </div>
            {/* Zona viva: el aviso aparece y desaparece sin mover el formulario. */}
            <div className={styles.hintSlot} aria-live="polite">
              {capsLock && <p className={styles.hint}>Tienes las mayúsculas activadas.</p>}
            </div>
          </div>

          <div aria-live="assertive" style={{ '--i': 6 } as CSSProperties}>
            {error && (
              <p className={styles.error} id="login-error">
                <span className={styles.errorIcon}>
                  <Icon name="info" size={15} />
                </span>
                <span>
                  {error.message}
                  {error.suggested && (
                    <button
                      type="button"
                      className={styles.errorAction}
                      onClick={() => switchAudience(error.suggested as LoginAudience)}
                    >
                      Cambiar a «{AUDIENCE_LABELS[error.suggested]}»
                    </button>
                  )}
                </span>
              </p>
            )}
          </div>

          <button
            className={styles.submit}
            style={{ '--i': 7 } as CSSProperties}
            type="submit"
            disabled={submitting}
          >
            <span className={styles.diamond} aria-hidden="true" />
            <span className={styles.submitLabel}>{submitting ? 'Comprobando…' : copy.cta}</span>
            <span className={styles.submitArrow} aria-hidden="true">
              <Icon name="arrow-right" size={17} />
            </span>
          </button>

          <p className={styles.foot} style={{ '--i': 8 } as CSSProperties}>
            {copy.foot}
          </p>

          {SHOW_DEMO_ACCOUNTS && (
            <aside className={styles.demo} style={{ '--i': 9 } as CSSProperties}>
              <p className={styles.demoTitle}>Cuentas de demostración</p>
              <div className={styles.demoList}>
                {demoAccounts.map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    className={styles.demoButton}
                    onClick={() => fillDemo(account.email)}
                    title={`Rellenar con ${account.email}`}
                  >
                    <span className={styles.demoEmail}>
                      {account.email}
                      <span className={styles.demoHint}>{account.hint}</span>
                    </span>
                    <span className={styles.demoRole}>{account.role}</span>
                  </button>
                ))}
              </div>
            </aside>
          )}
        </form>
      </main>
    </div>
  );
}

/**
 * El pico del logo, a escala de edificio.
 *
 * Se dibuja solo al entrar —los dos filetes se trazan con `stroke-dasharray`— y después se
 * queda quieto. Es decorativo puro: `aria-hidden`, sin texto alternativo y sin nada que
 * comunicar a quien no lo ve.
 */
function PeakMark() {
  return (
    <div className={styles.mark} aria-hidden="true">
      <svg viewBox="0 0 980 900" preserveAspectRatio="xMinYMax meet">
        <g fill="none" strokeLinejoin="miter">
          <path className={styles.peakA} d={PEAK_A} strokeWidth="118" />
          <path className={styles.peakB} d={PEAK_B} strokeWidth="118" />
          <path className={styles.ruleA} d={PEAK_A} strokeWidth="2.8" />
          <path className={styles.ruleB} d={PEAK_B} strokeWidth="2.8" />
        </g>
      </svg>
    </div>
  );
}
